import * as webllm from "@mlc-ai/web-llm";
// Type assertion to bypass TypeScript errors since WebLLM types might be incomplete
const webllmAny = webllm as any;

export class WebLLMService {
  private engine: any = null;
  private modelLoaded: boolean = false;
  private modelName: string;
  private loadingPromise: Promise<void> | null = null;
  private gpuAvailable: boolean = false;

  constructor() {
    this.modelName = import.meta.env.VITE_WEBLLM_MODEL || 'Qwen2.5-0.5B-Instruct-q4f16_1-MLC';
  }

  async initialize(): Promise<void> {
    // Expose instance immediately so LLMProviderService can access it while loading
    if (typeof window !== 'undefined') {
      (window as any).webllm = this;
    }

    if (this.modelLoaded) {
      return;
    }

    if (!this.loadingPromise) {
      this.loadingPromise = this.loadModel();
    }

    await this.loadingPromise;
  }

  private async loadModel(): Promise<void> {
    try {
      // Log configuration at startup
      this.logConfiguration();

      // Check if WebLLM library is available
      if (typeof window === 'undefined') {
        throw new Error('WebLLM cannot run in server environment');
      }

      // Check GPU capability
      this.gpuAvailable = await this.checkGPUSupport();
      console.log(`GPU Support: ${this.gpuAvailable ? 'Yes' : 'No (using CPU fallback)'}`);

      // Create engine with appropriate configuration
      const worker = new Worker(
        new URL('./webllm.worker.ts', import.meta.url),
        { type: 'module' }
      );

      this.engine = await webllmAny.CreateWebWorkerMLCEngine(worker, this.modelName, {
        initProgressCallback: (progress: any) => {
          console.log('WebLLM loading progress:', progress);
        }
      } as any);

      this.modelLoaded = true;
      console.log(`✅ WebLLM model ${this.modelName} loaded successfully`);
      console.log(`📊 Configuration: Offline Mode=${import.meta.env.VITE_OFFLINE_MODEL_ENABLED}, Model=${this.modelName}, GPU=${this.gpuAvailable ? 'Available' : 'Not Available'}`);
    } catch (error) {
      console.error('❌ Failed to load WebLLM model:', error);
      console.error('💡 Troubleshooting:');
      console.error('   - Ensure VITE_OFFLINE_MODEL_ENABLED=true in .env file');
      console.error('   - Check that your browser supports WebGPU (Chrome 113+, Edge 113+)');
      console.error('   - Verify the model name is correct');
      throw error;
    }
  }

  private async checkGPUSupport(): Promise<boolean> {
    try {
      // Check for WebGPU support
      if (navigator.gpu) {
        const adapter = await navigator.gpu.requestAdapter();
        if (adapter) {
          console.log('WebGPU adapter found:', adapter.toString() || 'Unknown GPU');
          return true;
        }
      }
      console.warn('WebGPU not available in this browser');
      return false;
    } catch (error) {
      console.warn('Error checking GPU support:', error);
      return false;
    }
  }

  private logConfiguration(): void {
    console.group('🔧 Application Configuration');
    console.log('Offline Mode Enabled:', import.meta.env.VITE_OFFLINE_MODEL_ENABLED === 'true');
    console.log('WebLLM Model:', this.modelName);
    console.log('STT Engine:', import.meta.env.VITE_STT_ENGINE || 'not configured');
    console.log('STT Model:', import.meta.env.VITE_WHISPER_MODEL || 'not configured');
    console.log('TTS Engine:', import.meta.env.VITE_TTS_ENGINE || 'not configured');
    console.log('TTS Voice:', import.meta.env.VITE_TTS_VOICE || 'default');
    console.log('Primary LLM Provider:', this.determinePrimaryProvider());
    console.groupEnd();
  }

  private determinePrimaryProvider(): string {
    const openrouterKey = import.meta.env.VITE_OPENROUTER_API_KEY;
    const openaiKey = import.meta.env.VITE_OPENAI_API_KEY;
    const apiUrl = import.meta.env.VITE_API_URL;

    if (openrouterKey && openrouterKey !== 'your_openrouter_api_key_here') {
      return 'OpenRouter';
    } else if (openaiKey && openaiKey !== 'your_openai_api_key_here') {
      return 'OpenAI';
    } else if (apiUrl) {
      return 'Ollama';
    } else {
      return 'Offline (WebLLM)';
    }
  }

  async generate(prompt: string, options: { max_tokens?: number; temperature?: number } = {}): Promise<string> {
    if (!this.modelLoaded) {
      await this.initialize();
    }

    try {
      const response = await this.engine.chat.completions.create({
        messages: [{ role: 'user', content: prompt }],
        max_tokens: options.max_tokens || 512,
        temperature: options.temperature || 0.7,
        stream: false
      });

      return response.choices[0].message.content || '';
    } catch (error) {
      console.error('WebLLM generation error:', error);
      throw error;
    }
  }

  async chat(messages: Array<{ role: string; content: string }>): Promise<string> {
    if (!this.modelLoaded) {
      await this.initialize();
    }

    try {
      const response = await this.engine.chat.completions.create({
        messages: messages.map(msg => ({
          role: msg.role as 'user' | 'assistant' | 'system',
          content: msg.content
        })),
        max_tokens: 512,
        temperature: 0.7,
        stream: false
      });

      return response.choices[0].message.content || '';
    } catch (error) {
      console.error('WebLLM chat error:', error);
      throw error;
    }
  }

  isModelLoaded(): boolean {
    return this.modelLoaded;
  }

  getModelName(): string {
    return this.modelName;
  }

  isGPUSupported(): boolean {
    return this.gpuAvailable;
  }

  async unload(): Promise<void> {
    if (this.engine) {
      await this.engine.unload();
      this.modelLoaded = false;
      this.engine = null;
      console.log('WebLLM model unloaded');
    }
  }
}
